import { useQuery } from "@tanstack/react-query";
import { fetchApiGet } from "../utils";

// ── Query parameter types ──────────────────────────────────────────

export interface AgtechTicketsExpandedFilters {
  customer_ids?: string;
  scale_ticket_id?: string;
  scale_ticket_inbound_outbound?: string;
  ship_date?: string;
  plc_id?: string;
  commodity_id?: string;
  ship_from?: string;
  ship_to?: string;
  page?: number;
  page_size?: number;
}

// ── Response types ─────────────────────────────────────────────────

export interface AgtechTicketsExpandedPagination {
  page: number;
  page_size: number;
  total_count: number;
  total_pages: number;
}

export type AgtechTicketsContractsInfo = AgtechTicketsContractInfo[];

export interface AgtechTicketsContractInfo {
  entity_type: string;
  contract_nbr: number;
  contract_id: number;
  pricing_type_code: number;
  contract_date: number;
  settlement_nbr: number;
  pricing_order: number;
  balance_forward: string;
  applied: string;
  gross_dollars: string;
  discounts: string;
  charges: string;
  deferred_payment: string;
  price: string;

  pricing?: { quantity: string; bot_uom: string; market_month: string } | null;
  pricingRelease?: { quantity: string; } | null;
  release?: { quantity: string; } | null;
}

export interface AgtechTicketsExpandedResponse {
  tickets: {
    data: ExpandedTransformedTicket[];
    pagination: AgtechTicketsExpandedPagination;
  };
  contractsInfo: AgtechTicketsContractsInfo
}

export interface ExpandedTransformedTicket {
  scale_ticket_id: string;
  entries: ExpandedTransformedEntry[];
}

export interface ExpandedTransformedEntry {
  base_uom: string;
  dest_uom: string;
  commodity_conversion_factor: string | null,
  quantity_entry_nbr: number;
  scale_ticket_id: string | null;
  scale_ticket_gross_weight: string | null;
  scale_ticket_tare_weight: string | null;
  scale_quantity_lb: string | null;
  vendor_id: string;
  customer_id: string | null;
  Customer: { long_name: string } | null;
  vehicle_id: string | null;
  plc_id: string;
  Location: { long_name: string } | null;
  commodity_id: string;
  commodity_display_name: string | null;
  reference_code: string | null;
  ship_date: string | null;
  add_date: string | null;
  scale_uom_code: string | null;
  report_uom_code: string | null;
  ship_from_id: string | null;
  ship_to_id: string | null;
  ShipFrom: { long_name: string } | null;
  ShipTo: { long_name: string } | null;
  grades: ExpandedChargeGradeDetail[];
  splits: TransformedSplit[];
  settlements: ExpandedTransformedSettlement[];
  advances: ExpandedTransformedAdvance[];
}

export interface TransformedSplit {
  ticket_related_id: string | null;
  split_percentage: string | null;
  scale_quantity_lb: string | null;
  TicketRelation?: { long_name: string } | null;
  applied_quantity_lb: string | null;
  shrink_quantity_lb: string | null;
  applied_quantity: string | null;
  shrink_quantity: string | null;
}

export interface ContractInfo {
  contract_id: number;
  vendor_customer_id: string;
  quantity: string | null;
  contract_type: string;
  pricing_type_code: string;
  contract_date: Date | null;
}

export interface ExpandedTransformedSettlement {
  settlement_nbr: number;
  serial_nbr: number | null;
  uom_code: string | null;
  contract_nbr: number;
  contract_id?: string | null;
  vehicle_disposition_nbr: number;
  pricing_order: number;
  vp_quantity: string | null;
  vp_applied_quantity: string | null;
  deleted_flag: string | null;
  ship_from_id: string | null;
  ship_to_id: string | null;
  ShipFrom: { long_name: string } | null;
  ShipTo: { long_name: string } | null;
  grades: ExpandedGrade[];
  contract?: ContractInfo | null;
  price: string | null;
  net_price: string | null;
  discounts_price: string | null;
  cash_charges: string | null;
  charges_details: string | null;
  cash_applied: string | null;
  cash_settlement_amount: string | null;
}

export interface ExpandedTransformedAdvance {
  advance_nbr: number;
  serial_nbr: number | null;
  uom_code: string | null;
  contract_nbr: number;
  contract_id?: string | null;
  vehicle_disposition_nbr: number;
  pricing_order: number;
  vp_quantity: string | null;
  vp_applied_quantity: string | null;
  deleted_flag: string | null;
  ship_from_id: string | null;
  ship_to_id: string | null;
  ShipFrom: { long_name: string } | null;
  ShipTo: { long_name: string } | null;
  grades: ExpandedGrade[];
  contract?: ContractInfo | null;
  price: string | null;
  net_price: string | null;
  discounts_price: string | null;
  cash_charges: string | null;
  charges_details: string | null;
  cash_applied: string | null;
  cash_advance_amount: string | null;
}

export interface ExpandedGrade {
  charge_grade_code: string;
  charge_grade_value: string | null;
}

export interface ExpandedChargeGradeDetail {
  charge_grade_code: string;
  charge_grade_value_o: string | null;
  charge_grade_value_d: string | null;
}

// ── Query param builder ────────────────────────────────────────────

function buildQueryParams(filters: AgtechTicketsExpandedFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.customer_ids) params.append("customer_ids", filters.customer_ids);
  if (filters.scale_ticket_id) params.append("scale_ticket_id", filters.scale_ticket_id);
  if (filters.scale_ticket_inbound_outbound) params.append("scale_ticket_inbound_outbound", filters.scale_ticket_inbound_outbound);
  if (filters.ship_date) params.append("ship_date", filters.ship_date);
  if (filters.plc_id) params.append("plc_id", filters.plc_id);
  if (filters.commodity_id) params.append("commodity_id", filters.commodity_id);
  if (filters.ship_from) params.append("ship_from", filters.ship_from);
  if (filters.ship_to) params.append("ship_to", filters.ship_to);
  if (filters.page !== undefined) params.append("page", filters.page.toString());
  if (filters.page_size !== undefined) params.append("page_size", filters.page_size.toString());

  return params;
}

// ── React Query hook ───────────────────────────────────────────────

export function useListAgtechTicketsExpanded(filters: AgtechTicketsExpandedFilters) {
  const queryParams = buildQueryParams(filters);

  return useQuery({
    queryKey: ["agtech", "tickets", "expanded", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechTicketsExpandedResponse>("agtech/tickets/expanded", queryParams),
    enabled: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: 1,
  });
}

// ── By-contract query ──────────────────────────────────────────────

export interface AgtechTicketsByContractFilters {
  contract_nbrs: number[];
  page?: number;
  page_size?: number;
}

function buildByContractQueryParams(filters: AgtechTicketsByContractFilters): URLSearchParams {
  const params = new URLSearchParams();

  params.append("contract_nbrs", filters.contract_nbrs.join(","));
  if (filters.page !== undefined) params.append("page", filters.page.toString());
  if (filters.page_size !== undefined) params.append("page_size", filters.page_size.toString());

  return params;
}

export function useListAgtechTicketsByContract(filters: AgtechTicketsByContractFilters) {
  const queryParams = buildByContractQueryParams(filters);

  return useQuery({
    queryKey: ["agtech", "tickets", "by-contract", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechTicketsExpandedResponse>("agtech/tickets/by-contract", queryParams),
    enabled: filters.contract_nbrs.length > 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    retry: 1,
  });
}

// ── By-settlement-or-advance query ─────────────────────────────────

export interface AgtechTicketsBySettlementOrAdvanceFilters {
  settlement_nbrs?: number[];
  advance_nbrs?: number[];
  page?: number;
  page_size?: number;
}

function buildBySettlementOrAdvanceQueryParams(filters: AgtechTicketsBySettlementOrAdvanceFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.settlement_nbrs && filters.settlement_nbrs.length > 0) {
    params.append("settlement_nbrs", filters.settlement_nbrs.join(","));
  }
  if (filters.advance_nbrs && filters.advance_nbrs.length > 0) {
    params.append("advance_nbrs", filters.advance_nbrs.join(","));
  }
  if (filters.page !== undefined) params.append("page", filters.page.toString());
  if (filters.page_size !== undefined) params.append("page_size", filters.page_size.toString());

  return params;
}

export function useListAgtechTicketsBySettlementOrAdvance(filters: AgtechTicketsBySettlementOrAdvanceFilters) {
  const queryParams = buildBySettlementOrAdvanceQueryParams(filters);

  const hasSettlements = filters.settlement_nbrs && filters.settlement_nbrs.length > 0;
  const hasAdvances = filters.advance_nbrs && filters.advance_nbrs.length > 0;

  return useQuery({
    queryKey: ["agtech", "tickets", "by-settlement-or-advance", queryParams.toString()],
    queryFn: () => fetchApiGet<AgtechTicketsExpandedResponse>("agtech/tickets/by-settlement", queryParams),
    enabled: hasSettlements || hasAdvances,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    retry: 1,
  });
}
